/* eslint-disable max-statements, no-param-reassign */
// @ts-check
import curry from 'ramda/src/curry';
import { debounce, throttle } from 'throttle-debounce';
import defaultTo from 'ramda/src/defaultTo';
import lensPath from 'ramda/src/lensPath';
import pipe from 'ramda/src/pipe';
import view from 'ramda/src/view';

import { fromNullable } from './utils';
import { fireEvents } from './events';
import { EVENT_TYPES, MODULE_SUB_TYPES } from './constants';

/** @typedef {import('./@types/pagetiger').OnlineMag} OnlineMag */
/** @typedef {import('./@types/pagetiger').VideoModule} VideoModule */

/**
 * @param {number} pageIndex - The PageTurn object
 * @returns {(param: OnlineMag) => any}
 */
const getPageModules = pageIndex =>
  pipe(view(lensPath(['pages', pageIndex, 'modules'])), defaultTo([]));

// =======
// HELPERS
// =======

/**
 * @param {OnlineMag} pageTurn - The PageTurn object
 * @param {Event} mediaEvent - The media event (video or audio)
 * @returns {any}
 */
const getVideo = (pageTurn, mediaEvent) => {
  if (
    mediaEvent.target instanceof HTMLVideoElement ||
    mediaEvent.target instanceof HTMLAudioElement
  ) {
    const { pageIndex = '0', videoId = '0' } = mediaEvent.target.dataset;
    const pageModules = getPageModules(Number(pageIndex))(pageTurn);
    const media = window.getByID(pageModules, Number(videoId));

    return fromNullable(media);
  }
};

/**
 * @param {any} chain - The PageTurn object
 * @returns {number}
 */
const getVideoEventTime = chain => parseInt(chain.eventArgs.option(Infinity));

/**
 * @param {import('./@types/pagetiger').VideoModule} video
 * @param {number} currentTime
 * @returns {number}
 */
const nextTimeChainAction = (video, currentTime) =>
  video.chain.reduce((acc, item) => {
    const eventTime = parseInt(item.eventArgs.option(0));

    if (eventTime > currentTime) {
      if (eventTime > acc) {
        return acc;
      }

      return eventTime;
    }

    return acc;
  }, Infinity);

const checkVideoTimeAndFireEvent = throttle(0, (video, currentTime) => {
  const videoTime = Math.floor(currentTime);

  video.chain
    .filter(chain => chain.eventTypeID === 4)
    .map(chain => {
      const chainActivationTime = getVideoEventTime(chain);
      if (
        !chain.blockFire &&
        chainActivationTime === videoTime &&
        getVideoEventTime(chain) === videoTime
      ) {
        fireEvents(EVENT_TYPES.ON_VIDEO_TIME, [chain]);
        chain.blockFire = true;
      }

      if (chainActivationTime !== videoTime) {
        chain.blockFire = false;
      }
    });
});

// ======
// EVENTS
// ======

const metaDataLoaded = curry((pageTurn, mediaEvent) => {
  getVideo(pageTurn, mediaEvent).map(media => {
    if (media.currentTime > 0) {
      mediaEvent.target.currentTime = media.currentTime;
    }
  });
});

const play = curry((pageTurn, mediaEvent) => {
  const mediaId = parseInt(mediaEvent.target.dataset.videoId);
  const media = window.getByID(pageTurn.allOnlineMagPageVideos, mediaId);

  if (media && media.videoPlayed) {
    media.videoPlayed();
  }

  getVideo(pageTurn, mediaEvent).map(mediaModule => {
    mediaModule.isPlaying = true;
    fireEvents(EVENT_TYPES.ON_SHOW, mediaModule.chain);
  });
});

const pause = curry(
  /**
   * @param {OnlineMag} pageTurn
   * @param {boolean} turning
   * @param {Event} mediaEvent
   * @returns {void}
   */
  (pageTurn, turning, mediaEvent) => {
    getVideo(pageTurn, mediaEvent).map(
      /** @param {VideoModule} media */
      media => {
        // The pause event fires when the media is removed from the page
        if (!turning) {
          media.isPlaying = false;
        }
      }
    );
  }
);

const bouncedEnded = debounce(500, true, (pageTurn, mediaEvent) => {
  getVideo(pageTurn, mediaEvent).map(media => {
    media.currentTime = 0;
    media.isPlaying = false;
    media.maxCurrentTimeProgress = mediaEvent.target.duration;
    media.hasBeenWatched = true;
    window.ptiVideoCompleted(pageTurn.containerElementID, media.id);

    fireEvents(EVENT_TYPES.ON_COMPLETE, media.chain);

    if (media.subType === MODULE_SUB_TYPES.VIDEO_POP_UP) {
      // ALWAYS:
      // 1) You close ptibox before firing any other events.
      // 2) You then fire any events
      $().ptibox.close();
    }
  });
});

const ended = curry((pageTurn, mediaEvent) => bouncedEnded(pageTurn, mediaEvent));

const seek = curry((pageTurn, mediaEvent) => {
  getVideo(pageTurn, mediaEvent).map(media => {
    if (media.hasBeenWatched) {
      return;
    }

    if (media.preventScrubbing && mediaEvent.target.currentTime > media.maxCurrentTimeProgress) {
      mediaEvent.target.currentTime = media.maxCurrentTimeProgress;

      return;
    }

    // Try and prevent the user from being able to skip past the next media chain
    if (mediaEvent.target.currentTime > media.nextChainTime) {
      mediaEvent.target.currentTime = media.nextChainTime;
    }
  });
});

const timelineUpdate = curry((pageTurn, mediaEvent) => {
  getVideo(pageTurn, mediaEvent).map(media => {
    const eventTime = mediaEvent.target.currentTime;
    const delta = eventTime - media.currentTime;

    if (mediaEvent.target.seeking && media.preventScrubbing) {
      mediaEvent.target.currentTime = media.currentTime;
      return;
    }

    // This is our best guess that the media is being played and not being scrubbed or skipped.
    if (mediaEvent.target.currentTime > media.maxCurrentTimeProgress && delta < 0.5) {
      media.maxCurrentTimeProgress = eventTime;
      media.currentTime = eventTime;
    }

    // If the user is seeking and trying to get past the next chain
    if (mediaEvent.target.seeking && eventTime >= media.nextChainTime) {
      mediaEvent.target.currentTime = Math.min(eventTime, media.nextChainTime);
      media.currentTime = media.nextChainTime;
      checkVideoTimeAndFireEvent(media, media.nextChainTime);
      return;
    }

    //If there are still chain events to deal with carry on
    if (media.nextChainTime !== Infinity && media.nextChainTime !== undefined) {
      checkVideoTimeAndFireEvent(media, eventTime);
    }

    media.nextChainTime = nextTimeChainAction(media, media.currentTime);
    media.currentTime = eventTime;
  });
});

export { ended, metaDataLoaded, play, pause, seek, timelineUpdate };
